import React, { useState, useEffect } from 'react';
import { FaUser, FaEnvelope, FaLock, FaUpload, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const AdminProfile = () => {
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get('https://app.bihebari.com.np/api/admin/profile', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setProfile({
                    name: response.data.data.name,
                    email: response.data.data.email,
                    image: response.data.data.image
                });
                if (response.data.data.image) {
                    setImagePreview(`https://app.bihebari.com.np/uploads/${response.data.data.image}`);
                }
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast.error('Error loading profile');
        }
    };

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfile({ ...profile, image: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const updateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        const formData = new FormData();
        formData.append('name', profile.name);
        if (profile.image && typeof profile.image !== 'string') {
            formData.append('image', profile.image);
        }
        
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put('https://app.bihebari.com.np/api/admin/profile', formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            toast.success('Profile updated successfully');
            fetchProfile();
        } catch (error) {
            toast.error('Error updating profile');
        } finally {
            setLoading(false);
        }
    };

    const updatePassword = async (e) => {
        e.preventDefault();
        
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('New passwords do not match');
            return;
        }
        
        if (passwordData.new_password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setLoading(true);
        
        try {
            const token = localStorage.getItem('adminToken');
            await axios.post('https://app.bihebari.com.np/api/admin/change-password', {
                current_password: passwordData.current_password,
                new_password: passwordData.new_password
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Password changed successfully');
            setPasswordData({
                current_password: '',
                new_password: '',
                confirm_password: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error changing password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Profile Information */}
            <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Profile Information</h2>
                
                <form onSubmit={updateProfile} className="space-y-6">
                    <div className="flex justify-center">
                        <div className="relative">
                            {imagePreview ? (
                                <img src={imagePreview} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                                    <FaUser className="text-3xl text-gray-400" />
                                </div>
                            )}
                            <label className="absolute bottom-0 right-0 bg-indigo-600 p-2 rounded-full cursor-pointer hover:bg-indigo-700">
                                <FaUpload className="text-white text-xs" />
                                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                            </label>
                        </div>
                    </div>
                    
                    <div>
                        <label className="form-label">Name</label>
                        <input
                            type="text"
                            name="name"
                            value={profile.name}
                            onChange={handleProfileChange}
                            className="form-input"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            value={profile.email}
                            className="form-input bg-gray-100 cursor-not-allowed"
                            disabled
                        />
                    </div>
                    
                    <button type="submit" disabled={loading} className="btn-primary w-full">
                        {loading ? <FaSpinner className="animate-spin mx-auto" /> : 'Update Profile'}
                    </button>
                </form>
            </div>

            {/* Change Password */}
            <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Change Password</h2>
                
                <form onSubmit={updatePassword} className="space-y-6">
                    <div>
                        <label className="form-label flex items-center">
                            <FaLock className="mr-2" />
                            Current Password
                        </label>
                        <input
                            type="password"
                            name="current_password"
                            value={passwordData.current_password}
                            onChange={handlePasswordChange}
                            className="form-input"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="form-label">New Password</label>
                        <input
                            type="password"
                            name="new_password"
                            value={passwordData.new_password}
                            onChange={handlePasswordChange}
                            className="form-input"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="form-label">Confirm New Password</label>
                        <input
                            type="password"
                            name="confirm_password"
                            value={passwordData.confirm_password}
                            onChange={handlePasswordChange}
                            className="form-input"
                            required
                        />
                    </div>
                    
                    <button type="submit" disabled={loading} className="btn-primary w-full">
                        {loading ? <FaSpinner className="animate-spin mx-auto" /> : 'Change Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminProfile;